'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Upload, File, Download, Trash2, HardDrive, RefreshCw } from 'lucide-react';

type FileData = {
  name: string;
  size: number;
  date: string;
  url: string;
};

export default function FileHub() {
  const [files, setFiles] = useState<FileData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchFiles = async () => {
    try {
      const res = await fetch('/api/files?type=upload');
      const data = await res.json();
      setFiles(data);
    } catch (e) {
      console.error("Failed to load files");
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    setIsUploading(true);
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/files', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        fetchFiles(); // Liste aktualisieren
      } else {
        alert("Upload fehlgeschlagen!");
      }
    } catch (e) {
      alert("Fehler beim Upload");
    } finally {
      setIsUploading(false);
      // Input resetten
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteFile = async (fileName: string) => {
    if (!confirm(`Soll ${fileName} wirklich gelöscht werden?`)) return;

    try {
      await fetch('/api/files', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName })
      });
      fetchFiles();
    } catch (e) {
      console.error("Delete failed");
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 shadow-2xl h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <HardDrive className="text-emerald-500 w-8 h-8" />
          <h2 className="text-2xl font-bold text-white uppercase tracking-wider">File Server</h2>
        </div>
        <button onClick={fetchFiles} className="text-slate-500 hover:text-white transition-colors">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Upload Area */}
      <div 
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed border-slate-700 rounded-lg p-6 mb-6 text-center cursor-pointer transition-all ${isUploading ? 'bg-slate-800 animate-pulse' : 'hover:bg-slate-800 hover:border-emerald-500'}`}
      >
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleUpload} 
          className="hidden" 
        />
        {isUploading ? (
          <p className="text-emerald-400 font-bold">Lade hoch...</p>
        ) : (
          <div className="text-slate-400">
            <Upload className="w-8 h-8 mx-auto mb-2 text-slate-500" />
            <p className="font-bold">Datei hier ablegen oder klicken</p>
            <p className="text-xs mt-1 text-slate-600">Maps, Patches, Memes (Max 500MB)</p>
          </div>
        )}
      </div>

      {/* File List */}
      <div className="flex-1 overflow-y-auto space-y-2 pr-2">
        {files.length === 0 ? (
          <p className="text-center text-slate-600 italic mt-8">Keine Dateien auf dem Server.</p>
        ) : (
          files.map((file) => (
            <div key={file.name} className="flex items-center justify-between p-3 rounded bg-slate-950/50 border border-slate-800 hover:border-slate-600 transition-all group">
              <div className="flex items-center gap-3 overflow-hidden">
                <File className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-200 truncate">{file.name}</p>
                  <p className="text-xs text-slate-500">{formatSize(file.size)}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 flex-shrink-0">
                <a 
                  href={`/api/download?file=${encodeURIComponent(file.name)}`}
                  download
                  className="p-2 text-slate-400 hover:text-emerald-400 transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button 
                  onClick={() => deleteFile(file.name)}
                  className="p-2 text-slate-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                  title="Löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
